﻿import React, { useState, useEffect } from "react";
import TickerPicker from "./TickerPicker.jsx";
import Sparkline from "./Sparkline.jsx";
import { api } from "../utils/api.js";
import { getStockStats, refreshSummaries, useSummariesVersion } from "../utils/priceData.js";
import { getCompany, useCompanyInfoVersion } from "../utils/companyInfo.js";
import { fmtMetric, unitSuffix } from "../utils/fmtMetric.js";

export default function CompareCompanies() {
  useSummariesVersion();
  useCompanyInfoVersion();
  const [picks, setPicks] = useState(["AAPL", "MSFT", ""]);
  const [fields, setFields] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [error, setError] = useState("");

  const setPick = (i, value) => setPicks(picks.map((p, idx) => (idx === i ? value : p)));
  const chosen = [...new Set(picks.filter(Boolean))];
  const key = chosen.join(",");

  useEffect(() => {
    api("/screener/fields").then((f) => setFields(f.filter((x) => x.type === "num"))).catch(() => {});
  }, []);

  // load the numbers, and reload every minute
  useEffect(() => {
    if (!key) {
      setMetrics({});
      return undefined;
    }
    let dead = false;
    const load = () => {
      refreshSummaries(key.split(","));
      api(`/metrics?tickers=${encodeURIComponent(key)}`)
        .then((rows) => {
          if (dead) return;
          setMetrics(Object.fromEntries(rows.map((r) => [r.ticker, r])));
          setError("");
        })
        .catch((e) => !dead && setError(e.message));
    };
    load();
    const id = setInterval(load, 60000);
    return () => {
      dead = true;
      clearInterval(id);
    };
  }, [key]);

  const groups = [];
  for (const f of fields) {
    // hide rows where none of the chosen companies has a value
    if (!chosen.some((t) => metrics[t] && metrics[t][f.key] !== null && metrics[t][f.key] !== undefined)) continue;
    let g = groups.find((x) => x.name === f.group);
    if (!g) groups.push((g = { name: f.group, rows: [] }));
    g.rows.push(f);
  }

  return (
    <main className="page-bg">
      <div className="dash-main-content screens-wrap">
        <h1 className="page-title">Compare Companies</h1>
        <p className="text-muted mb-4">Pick up to three US companies and compare them side by side.</p>

        <div className="card tool-card mb-4">
          <div className="tool-form tool-form-3">
            {picks.map((p, i) => (
              <div key={i}>
                <label className="tool-label">Company {i + 1}{i === 2 ? " (optional)" : ""}</label>
                <TickerPicker value={p} onChange={(v) => setPick(i, v)} allowClear={i > 0} />
              </div>
            ))}
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {chosen.length > 0 && (
          <div className="card wl-table-card">
            <div className="table-responsive">
              <table className="table wl-table mb-0">
                <thead>
                  <tr>
                    <th className="wl-th-plain">Metric</th>
                    {chosen.map((t) => {
                      const c = getCompany(t);
                      return (
                        <th key={t} className="text-end">
                          <a className="wl-company" href={`#/stock/${t}`}>{c ? c.name : t}</a>
                          <span className="text-muted small ms-2">{t}</span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Sector</td>
                    {chosen.map((t) => <td key={t} className="text-end">{(getCompany(t) || {}).sector || "—"}</td>)}
                  </tr>
                  <tr>
                    <td>Industry</td>
                    {chosen.map((t) => <td key={t} className="text-end">{(getCompany(t) || {}).industry || "—"}</td>)}
                  </tr>
                  <tr>
                    <td>52W chart</td>
                    {chosen.map((t) => (
                      <td key={t} className="text-end"><Sparkline data={getStockStats(t).series} /></td>
                    ))}
                  </tr>
                  {groups.map((g) => (
                    <React.Fragment key={g.name}>
                      <tr>
                        <td colSpan={chosen.length + 1} className="fw-bold" style={{ background: "#f7f5fb" }}>{g.name}</td>
                      </tr>
                      {g.rows.map((f) => (
                        <tr key={f.key}>
                          <td>{f.label}{unitSuffix(f.unit)}</td>
                          {chosen.map((t) => (
                            <td key={t} className="text-end">{fmtMetric(metrics[t] ? metrics[t][f.key] : null, f.unit)}</td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-muted small wl-note">
              Prices come from your database (about 15 minutes delayed). Financial figures are calculated from SEC filings and update after each filing.
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
